import { action, computed, makeObservable, observable } from 'mobx';
import debounce from 'lodash/debounce';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { IPlayer } from './playerSchema';

type Props = BindingProps<{
  controller: IPlayer
}>

type PlayerChromeVisibility =
  'Hidden' |
  'Compact' |
  'Visible';

const CHROME_HIDE_TIMEOUT = 3000;

export class PlayerChromeState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'hideAfterDebounce'>(this, {
      controller: computed,
      visibility: observable,
      showAllRegions: computed,
      showCriticalRegions: computed,
      isPinned: observable,
      isHovering: observable,
      isRegionHovering: observable,
      isVideoHovering: observable,
      isControlActionActive: observable,
      lastActivityTimestamp: observable,
      pin: action,
      unpin: action,
      togglePin: action,
      hideAfterDebounce: action,
      startControlAction: action,
      endControlAction: action,
    });
  }

  get controller(): IPlayer | null {
    return this.resolvedProps.controller ?? null;
  }

  visibility: PlayerChromeVisibility = 'Visible';

  get showAllRegions() {
    return this.visibility === 'Visible';
  }
  get showCriticalRegions() {
    return (
      this.visibility === 'Compact' ||
      this.visibility === 'Visible');
  }

  isPinned: boolean = false;
  isHovering: boolean = false;
  isRegionHovering: boolean = false;
  isVideoHovering: boolean = false;
  isControlActionActive: boolean = false;
  lastActivityTimestamp: number = 0;

  handlePointerMove = action(() => {
    this.visibility = 'Visible';
    this.handleEventDebounced();
  })

  handlePointerEnter = action(() => {
    this.isHovering = true;
  })

  handlePointerLeave = action(() => {
    this.isHovering = false;

  })

  handleTouchStart = action(() => {
    this.visibility = 'Visible';
    this.isVideoHovering = true;
    this.handleEventDebounced();
  })

  handleRegionPointerEnter = action(() => {
    this.isRegionHovering = true;
  })
  handleRegionPointerLeave = action(() => {
    this.isRegionHovering = false;
  });


  handleVideoPointerEnter = action(() => {
    this.isVideoHovering = true;
  })
  handleVideoPointerLeave = action(() => {
    this.isVideoHovering = false;
  })
  handleVideoClick = action(() => {
    this.controller?.invoke('togglePlay');
  })
  handleVideoDoubleClick = action(() => {
    this.controller?.invoke('toggleFullscreen');
  });

  toggleVisibility = action((visibility: PlayerChromeVisibility) => {
    this.visibility = visibility;

    if (visibility === 'Visible') this.handleRegionPointerEnter();
    if (visibility === 'Compact') this.handleRegionPointerLeave();
  });

  pin() {
    this.isPinned = true;
  }

  unpin() {
    this.isPinned = false;
  }

  togglePin() {
    if (this.isPinned)
      this.unpin();
    else
      this.pin();
  }

  private handleEventDebounced = debounce(() => this.hideAfterDebounce(),
    CHROME_HIDE_TIMEOUT);

  private hideAfterDebounce(): boolean {
    if (this.isRegionHovering || this.isPinned || this.isControlActionActive) {
      this.handleEventDebounced()
      return false;
    }

    this.visibility = 'Compact';
    return true;
  }

  startControlAction = () => { //this is a reactive observer to control the player layout based on component level states
    this.isControlActionActive = true;
  }

  endControlAction = () => {
    this.isControlActionActive = false;
  }
}